import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Filter, Building2, Phone, Mail, TrendingUp, Package, FileText, Plus } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { professionalClientService } from '../../services/professionalClientService';

const ProfessionalClientsManagementDashboard = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadClients();
  }, [searchTerm, clientTypeFilter, statusFilter, sortBy, sortOrder]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await professionalClientService?.getAllClients({
        searchTerm,
        clientType: clientTypeFilter,
        status: statusFilter,
        sortBy,
        sortOrder
      });
      setClients(data);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des clients professionnels');
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const getClientTypeLabel = (type) => {
    const labels = {
      merchant: 'Commerçant',
      association: 'Association',
      company: 'Entreprise',
      moving: 'Déménagement',
      other: 'Autre'
    };
    return labels?.[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      active: 'Actif',
      pending: 'En attente',
      inactive: 'Inactif'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles?.[status] || styles?.inactive}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <BreadcrumbTrail customItems={[
          { label: 'Administration', path: '/admin-shipments-dashboard' },
          { label: 'Clients Pro', path: '/professional-clients-management-dashboard' }
        ]} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="ml-4 text-gray-600">Chargement des clients professionnels...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <BreadcrumbTrail customItems={[
        { label: 'Administration', path: '/admin-shipments-dashboard' },
        { label: 'Clients Pro', path: '/professional-clients-management-dashboard' }
      ]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-green-600" />
                Gestion Clients Pro
              </h1>
              <p className="mt-2 text-gray-600">
                {clients?.length} client{clients?.length > 1 ? 's' : ''} professionnel{clients?.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/professional-client-register')}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-5 h-5 mr-2" />
                Ajouter un client pro
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  placeholder="Rechercher par nom, email..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Client Type Filter */}
            <div>
              <select
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous les types</option>
                <option value="merchant">Commerçant</option>
                <option value="association">Association</option>
                <option value="company">Entreprise</option>
                <option value="moving">Déménagement</option>
                <option value="other">Autre</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="pending">En attente</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expéditions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">Aucun client professionnel trouvé</p>
                    <p className="text-sm mt-1">Ajoutez votre premier client professionnel pour commencer</p>
                  </td>
                </tr>
              ) : (
                clients?.map((client) => (
                  <tr key={client?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">{client?.companyName}</p>
                        {client?.siret && (
                          <p className="text-sm text-gray-500">SIRET: {client?.siret}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-gray-900">{client?.contactName}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <Mail className="w-3 h-3" />
                          {client?.email}
                        </div>
                        {client?.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Phone className="w-3 h-3" />
                            {client?.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getClientTypeLabel(client?.clientType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{client?.totalShipments || 0}</span>
                        </div>
                        {client?.totalRevenue > 0 && (
                          <div className="flex items-center gap-1">
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-sm font-medium text-gray-900">{client?.totalRevenue?.toFixed(2)}€</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(client?.accountStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/detailed-professional-client-profile?id=${client?.id}`)}
                        className="text-green-600 hover:text-green-800 font-medium text-sm flex items-center gap-1"
                      >
                        <FileText className="w-4 h-4" />
                        Voir fiche
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Quick Stats */}
        {clients?.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{clients?.length}</p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Clients Actifs</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {clients?.filter(c => c?.accountStatus === 'active')?.length}
                  </p>
                </div>
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Expéditions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {clients?.reduce((sum, c) => sum + (c?.totalShipments || 0), 0)}
                  </p>
                </div>
                <Package className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Revenu Total</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {clients?.reduce((sum, c) => sum + (c?.totalRevenue || 0), 0)?.toFixed(2)}€
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalClientsManagementDashboard;